import { generatePath, matchPath } from 'react-router';
import { call, put, takeEvery, takeLatest, select, debounce, all, take } from 'redux-saga/effects';

import * as navigateActions from 'actions/navigateActions';
import * as userAction from 'actions/userActions';
import { restClient } from 'config/store/configureStore';
import * as types from 'constants/ActionTypes';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { SELECTED_DEALER_ID, SELECTED_GROUP_ID } from 'constants/FilterConstants';
import { DEBOUNCE_TIME, MAX_RECORD } from 'constants/Global';
import { PATH, ROUTES } from 'constants/RoutesConstants';
import { APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { applicationIsReady } from 'modules/app/selectors';
import { signOut } from 'modules/auth/actions';
import * as authTypes from 'modules/auth/actionTypes';
import { getUserIdFromAuthSession } from 'modules/auth/utils';
import { fetchDealersSelect } from 'modules/dealers/actions';
import { fetchGroup, fetchGroupsSelect } from 'modules/groups/actions';
import { getLocale } from 'modules/i18n/selectors/data';
import { navigate } from 'modules/router/navigate';
import { getLocation } from 'modules/router/selectors';
import { createNotification } from 'modules/toast-notifications/actions';
import { fetchUserGroupsByDealerIds } from 'modules/userGroups/actions';

import { CURRENT_USER_DATA_REQUEST } from 'modules/users/actionTypes';
import { getParams } from 'modules/users/selectors';
import { getCurrentUserAdditionalData, getCurrentUserId } from 'modules/users/selectors/currentUser';
import { getSelectedDealer } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';
import { getAssignedUsersLinks } from 'selectors/userSelectors';
import UserService from 'services/api/UserService';

const DETAILS_PATHS = [PATH.USERS_DETAILED, PATH.PROFILE];

/* GET USERS */
function* fetchFilteredUsers() {
  const params = yield select(getParams);
  yield put(userAction.fetchUsers({ ...params, page: 0 }, { source: 'dashboard' }));
}

function* fetchUsers({ payload, meta = {} }) {
  const { request } = payload;

  try {
    const { data: users } = yield call(restClient.request, request);
    yield put(userAction.fetchUsersSuccess(users, meta));
  } catch (e) {
    yield put(userAction.fetchUsersFailure(e));
  }
}

function* initApplication() {
  const dealer = yield select(getSelectedDealer);
  const newFilters = {
    [SELECTED_DEALER_ID]: dealer?.id,
    [SELECTED_GROUP_ID]: dealer?.group?.id ?? dealer?.groupId,
  };
  yield put(userAction.setUserFilter(newFilters));
}

function* fetchUser({ payload, meta }) {
  const { request, userId } = payload;

  try {
    const { data: user } = yield call(restClient.request, request);
    yield put(userAction.fetchUserSuccess(user, meta));
  } catch (error) {
    yield put(userAction.fetchUserFailure(error, { ...meta, userId }));
  }
}

function* fetchCurrentUserAfterLogInWorker({ payload: tokens }) {
  const userId = getUserIdFromAuthSession(tokens);
  yield put(userAction.fetchDataForCurrentUser(userId));

  const isStarted = yield select(applicationIsReady);
  if (!isStarted) {
    yield take(APPLICATION_IS_READY);
  }

  // Trigger a LOCATION_CHANGE so every needed data is fetched.
  const location = yield select(getCurrentLocation);
  yield call(navigate, location);
}

function* fetchCurrentUserAfterLoadingSessionWorker({ payload: tokens }) {
  const userId = getUserIdFromAuthSession(tokens);
  yield put(userAction.fetchDataForCurrentUser(userId));
}

function* fetchCurrentUser({ payload: currentUserId, meta }) {
  try {
    const response = yield call(UserService.getUser, currentUserId);
    yield put(userAction.fetchDataForCurrentUserSuccess(response.data, meta));
  } catch (e) {
    yield put(userAction.fetchDataForCurrentUserFailure(e, meta));
  }
}

function* updateUserWorker({ payload, meta }) {
  const userData = yield select(getCurrentUserAdditionalData);

  try {
    const { data: user } = yield call(restClient.request, payload.request);
    yield put(userAction.updateUserSuccess(user, meta));

    yield put(
      createNotification({
        type: 'success',
        message: { id: 'page.user.notification.update.success', defaultMessage: 'User information updated' },
      })
    );
    if (userData?.id === payload.id && payload.setPassword) {
      yield put(signOut({ userId: userData.id, token: userData.token }));
    }
  } catch (error) {
    yield put(userAction.updateUserFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.user.notification.update.failure',
          defaultMessage: 'An error occurred. Changes have not been saved.',
        },
      })
    );
  }
}

function* createUserWorker({ payload, meta }) {
  const { origin } = meta;
  try {
    const { data } = yield call(UserService.createUser, payload);
    yield put(userAction.createUserSuccess({ payload: data }));
    yield put(
      createNotification({
        type: 'success',
        message: { id: 'page.user.notification.update.success', defaultMessage: 'User information updated' },
      })
    );
    if (origin === ROUTES.DEALER_MANAGEMENT) {
      yield call(navigateActions.navigateToManagement);
    } else {
      yield call(navigate, generatePath(PATH.USERS_DETAILED.path, { userId: data.id }), { replace: true });
    }
  } catch (error) {
    yield put(userAction.createUserFailure({ error }));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.user.notification.create.failure',
          defaultMessage: 'An error occurred. Changes have not been saved',
        },
      })
    );
  }
}

function* fetchAssignedUsers({ payload }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(userAction.fetchAssignedUsersSuccess(response.data));
  } catch (error) {
    yield put(userAction.fetchAssignedUsersFailure(error));
  }
}

function* fetchAssignedUsersNextPage() {
  const links = yield select(getAssignedUsersLinks);
  const request = {
    method: 'GET',
    url: links.next.href,
  };
  try {
    const response = yield call(restClient.request, request);
    yield put(userAction.fetchAssignedUsersSuccess(response.data));
  } catch (error) {
    yield put(userAction.fetchAssignedUsersFailure(error));
  }
}

function* fetchUsernameExist({ payload }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(userAction.userExistsSuccess(true));
  } catch (error) {
    if (error.response.status === 404) {
      yield put(userAction.userExistsSuccess(false));
    } else {
      yield put(userAction.userExistsFailure(error));
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: 'http.error.message',
            defaultMessage: 'Oops! Something went wrong.',
          },
        })
      );
    }
  }
}

function* afterSelectDealerInDashboard() {
  const location = yield select(getCurrentLocation);
  const isReady = yield select(applicationIsReady);
  const match = matchPath(PATH.USERS.path, location);

  if (isReady && match) {
    const dealer = yield select(getSelectedDealer);
    const newFilters = {
      [SELECTED_DEALER_ID]: dealer?.id,
      [SELECTED_GROUP_ID]: dealer?.group?.id ?? dealer?.groupId,
    };
    yield put(userAction.setUserFilter(newFilters));
  }
}

function* getDetailedUserId() {
  const { pathname } = yield select(getLocation);
  const route = DETAILS_PATHS.find(path => matchPath(path.path, pathname) !== null);
  const match = matchPath(route.path, pathname);
  const { userId } = match.params;
  return userId;
}

function* loadUserCommon() {
  const locale = yield select(getLocale);
  yield putAndWait(
    fetchGroupsSelect({
      reducer: ['id,none', 'name,none'],
      sort: 'name,asc',
      lang: locale,
    })
  );
}

// Load all about user's dealers
function* initUserDealers(dealerIds) {
  if (dealerIds?.length > 0) {
    yield putAndWait(
      fetchDealersSelect(undefined, {
        page: 0,
        ids: dealerIds,
        size: MAX_RECORD,
        projection: 'details',
        fields: 'id,name,group,group.data,group.name,status,contractProperties,contractProperties.contractCode,config,config.*,features,extensions',
      })
    );
  }
}

// Load all about user's dealers
function* initDealerUserGroups(dealerIds) {
  if (dealerIds?.length > 0) {
    yield putAndWait(fetchUserGroupsByDealerIds({ dealerIds }));
  }
}

// Load information about user's groups
function* initUserGroups(groupId) {
  if (groupId) {
    yield putAndWait(fetchGroup(groupId));
  }
}

function* loadUserDetails(userId) {
  const { payload: user, error } = yield putAndWait(userAction.fetchUser(userId));
  if (!error) {
    // Fetch all user groups for given dealerIds
    yield all([
      call(initUserGroups, user.groupId),
      call(initUserDealers, user.dealerIds),
      call(initDealerUserGroups, user.dealerIds),
    ]);
  }
}

function* initUserDetailsWorker() {
  const userId = yield call(getDetailedUserId);
  yield call(loadUserCommon);

  if (userId !== NEW_ENTITY_ID) {
    // Case of the current user profile is displaying
    const currentUserId = yield select(getCurrentUserId);
    yield call(loadUserDetails, userId ?? currentUserId);
  }
  yield put(userAction.isDetailsPageReady());
}

export default function* userSagas() {
  yield takeLatest(types.FETCH_USER_REQUEST, fetchUser);
  yield takeEvery(types.FETCH_USERS_REQUEST, fetchUsers);
  yield takeLatest(CURRENT_USER_DATA_REQUEST, fetchCurrentUser);
  yield takeLatest(authTypes.SIGN_IN_SUCCESS, fetchCurrentUserAfterLogInWorker);
  yield takeLatest(authTypes.LOAD_SESSION_SUCCESS, fetchCurrentUserAfterLoadingSessionWorker);

  yield debounce(DEBOUNCE_TIME, types.USER_EXISTS_REQUEST, fetchUsernameExist);

  yield takeLatest(types.FETCH_ASSIGNED_USERS, fetchAssignedUsers);
  yield takeLatest(types.FETCH_ASSIGNED_USERS_NEXT_PAGE, fetchAssignedUsersNextPage);

  yield takeLatest(types.CREATE_USER_REQUEST, createUserWorker);
  yield takeLatest(types.UPDATE_USER_REQUEST, updateUserWorker);

  yield takeEvery(types.INIT_USERS_DASHBOARD, initApplication);
  yield takeEvery(types.SELECT_DEALER, afterSelectDealerInDashboard);
  yield takeLatest([types.REMOVE_USER_FILTER, types.RESET_USER_FILTER], fetchFilteredUsers);
  yield debounce(DEBOUNCE_TIME, types.SET_USERS_FILTER, fetchFilteredUsers);

  yield scopeToPage([PATH.USERS_DETAILED, PATH.PROFILE], initUserDetailsWorker);
}
